import { arrondiCentime } from './arrondi.js';
import type { ParamsProjection, PointSerie, FormeFrais } from './types.js';

/**
 * Net annual rate from gross return and fee.
 * Multiplicative form (default): r_net = (1 + r) * (1 - f) - 1 (fees on outstanding).
 * Subtractive form: r_net = r - f.
 */
export function tauxNetAnnuel(
  rendementBrut: number,
  fraisAnnuels: number,
  forme: FormeFrais = 'multiplicatif',
): number {
  return forme === 'soustractif'
    ? rendementBrut - fraisAnnuels
    : (1 + rendementBrut) * (1 - fraisAnnuels) - 1;
}

/** Equivalent monthly rate from a net annual rate. */
export function tauxMensuel(rNet: number): number {
  if (rNet <= -1) {
    throw new RangeError('tauxMensuel: net annual rate must be greater than -1');
  }
  return Math.pow(1 + rNet, 1 / 12) - 1;
}

/**
 * Monthly projection over H years (M = 12 * H). Matches the reference engine in
 * `assets/donnees-demo.js`: capital and cumulative contributions are rounded to the cent
 * at each step and fed back, so the series is bit-for-bit identical to the prototype.
 *
 * K(0) = V0 ; C(0) = V0
 * flux(m) = Vm + sum of exceptional flows at month m
 * K(m) = round(K(m-1) * (1 + i) + flux(m))
 * C(m) = round(C(m-1) + flux(m))
 */
export function projeter(params: ParamsProjection): PointSerie[] {
  validerParams(params);
  const rNet = tauxNetAnnuel(params.rendementBrut, params.fraisAnnuels, params.formeFrais);
  const i = tauxMensuel(rNet);
  const M = Math.round(params.horizonAnnees * 12);

  let K = arrondiCentime(params.v0);
  let C = arrondiCentime(params.v0);
  const serie: PointSerie[] = [{ mois: 0, capital: K, cumul: C }];

  for (let m = 1; m <= M; m++) {
    const fx = (params.flux ?? [])
      .filter((f) => f.mois === m)
      .reduce((somme, f) => somme + f.montant, 0);
    const flux = params.vm + fx;
    K = arrondiCentime(K * (1 + i) + flux);
    C = arrondiCentime(C + flux);
    serie.push({ mois: m, capital: K, cumul: C });
  }
  return serie;
}

/**
 * Annual sampling of a monthly series for the chart: A1..AN, each point taken at
 * month min(a * 12, last). Returned amounts stay in euros; the UI converts to k euros.
 */
export function pointsAnnuels(serie: readonly PointSerie[], horizonAnnees: number): PointSerie[] {
  const dernier = serie.length - 1;
  const points: PointSerie[] = [];
  for (let a = 1; a <= horizonAnnees; a++) {
    const point = serie[Math.min(a * 12, dernier)];
    if (point !== undefined) {
      points.push(point);
    }
  }
  return points;
}

function validerParams(params: ParamsProjection): void {
  if (!Number.isFinite(params.v0) || params.v0 < 0) {
    throw new RangeError('projeter: v0 must be a finite number >= 0');
  }
  if (!Number.isFinite(params.vm) || params.vm < 0) {
    throw new RangeError('projeter: vm must be a finite number >= 0');
  }
  if (
    !Number.isInteger(params.horizonAnnees) ||
    params.horizonAnnees < 1 ||
    params.horizonAnnees > 40
  ) {
    throw new RangeError('projeter: horizonAnnees must be an integer in [1, 40]');
  }
  if (
    !Number.isFinite(params.fraisAnnuels) ||
    params.fraisAnnuels < 0 ||
    params.fraisAnnuels >= 1
  ) {
    throw new RangeError('projeter: fraisAnnuels must be in [0, 1)');
  }
  if (!Number.isFinite(params.rendementBrut) || params.rendementBrut <= -1) {
    throw new RangeError('projeter: rendementBrut must be greater than -1');
  }
}
